import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { RichText, htmlToText } from "@/components/RichText";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CONTENT_META = [
  { key: "olympiad_name", label: "Olympiad brand name", group: "Branding", def: "Olympiad" },
  { key: "olympiad_tagline", label: "Olympiad tagline (Secure Exams page)", group: "Branding", def: "Olympiad-grade, locked-down assessments" },
  { key: "secure_exams_title", label: "Secure Exams page title", group: "Branding", def: "Secure Exams" },
  { key: "landing_hero_line1", label: "Hero heading — line 1", group: "Landing Page", def: "Transform Your" },
  { key: "landing_hero_highlight", label: "Hero heading — highlighted word", group: "Landing Page", def: " Learning" },
  { key: "landing_hero_line2", label: "Hero heading — line 2", group: "Landing Page", def: "Experience" },
  { key: "landing_hero_subtitle", label: "Hero subtitle paragraph", group: "Landing Page", def: "Your all-in-one smart learning platform. Join classes, collaborate effortlessly, share resources, run quizzes, and track real-time progress—all in one place." },
  { key: "student_welcome_subtitle", label: "Welcome subtitle", group: "Student Dashboard", def: "Here's what's happening in your classes" },
  { key: "student_arena_button", label: "Arena button label", group: "Student Dashboard", def: "Times Tables Arena" },
  { key: "student_ai_button", label: "AI tutor button label", group: "Student Dashboard", def: "AI Doubt Solver" },
  { key: "teacher_welcome_subtitle", label: "Welcome subtitle", group: "Teacher Dashboard", def: "Manage your classes and students" },
  { key: "teacher_reports_title", label: "Reported problems card title", group: "Teacher Dashboard", def: "Reported Problems" },
  { key: "teacher_reports_subtitle", label: "Reported problems card subtitle", group: "Teacher Dashboard", def: "Question issues reported by students during Olympiad exams" },
  { key: "teacher_media_library_button", label: "Media Library button label", group: "Teacher Dashboard", def: "Media Library" },
  { key: "teacher_secure_exams_button", label: "Secure Exams button label", group: "Teacher Dashboard", def: "Secure Exams" }
];

const DEFAULTS = Object.fromEntries(CONTENT_META.map(m => [m.key, m.def]));

const SiteContentContext = createContext({ overrides: {}, ct: (k) => DEFAULTS[k] ?? k, ctHtml: (k) => DEFAULTS[k] ?? k, refresh: () => {} });

export function SiteContentProvider({ children }) {
  const [overrides, setOverrides] = useState({});

  const refresh = async () => {
    try {
      const res = await axios.get(`${API}/site-content`);
      setOverrides(res.data || {});
    } catch (e) { /* defaults still apply */ }
  };

  useEffect(() => { refresh(); }, []);

  // ctHtml: raw (possibly rich HTML) value; ct: plain-text version for strings/PDFs/attributes
  const ctHtml = (key) => {
    const v = overrides[key];
    return (v !== undefined && v !== null && v !== "") ? v : (DEFAULTS[key] ?? key);
  };
  const ct = (key) => htmlToText(ctHtml(key));
  const mobileSize = (key) => overrides._mobile_font_sizes?.[key] || null;

  return (
    <SiteContentContext.Provider value={{ overrides, ct, ctHtml, mobileSize, refresh }}>
      {children}
    </SiteContentContext.Provider>
  );
}

export function useContent() {
  return useContext(SiteContentContext);
}

// Renders an editable content key with its rich formatting (inline by default, block for paragraphs)
export function CT({ k, block = false, className = "", trim = false }) {
  const { ctHtml, mobileSize } = useContent();
  const v = ctHtml(k);
  const m = mobileSize ? mobileSize(k) : null;
  return (
    <RichText
      html={trim ? v.trim() : v}
      inline={!block}
      className={`${m ? "m-fs" : ""} ${className}`}
      style={m ? { "--m-fs": `${m}px` } : undefined}
      data-testid={`ct-${k}`}
    />
  );
}
